import moment from 'moment';

import { isEmptyObject } from '@philips-emr/tws-utils';

export interface MessageServiceConfig {
  defaultLanguage: string;
  languages: Record<string, string>;
}

const defaultConfig: MessageServiceConfig = {
  defaultLanguage: 'en-us',
  languages: {},
};

class MessageService {

  private config: MessageServiceConfig = defaultConfig;
  private current: string = '';
  private data: Record<string, string> = {};
  private initialized = false;

  /**
   * Initializes the message service. This can only be done once.
   * @param config The message service configuration, as provided by **tws-builder**.
   */
  init(config: MessageServiceConfig): Promise<void> {
    if (this.initialized) {
      throw new Error('[Message service]: already initialized.');
    }
    this.initialized = true;
    this.config = normalizeConfig(config);

    return this.changeLanguage();
  }

  /**
   * Changes the current language.
   * @param language The language key to change to.
   */
  async changeLanguage(language: string = getBrowserLanguage()): Promise<void> {
    const languages = this.config.languages;
    const chosenLanguage = normalizeLanguageKey(language) in languages
      ? normalizeLanguageKey(language)
      : normalizeLanguageKey(this.config.defaultLanguage);

    if (this.current === chosenLanguage) {
      return;
    }

    const deltas = await fetchLanguageDeltas(languages, chosenLanguage);
    const data = Object.assign({}, ...deltas.map(delta => delta.data));

    moment.locale(language);

    // only changes stats when everything went ok
    this.current = chosenLanguage;
    this.data = data;
  }

  /**
   * Retrieves the current language key.
   */
  getCurrentLanguage(): string {
    return this.current;
  }

  /**
   * Translates an expression into its corresponding text for the current language.
   */
  msg = (expression: string, args: Record<string, any> = {}): string => {
    if (expression in this.data) {
      return interpolate(this.data[expression], args);
    }
    return interpolate(expression, args);
  };
}

const instance = new MessageService();
const msg = instance.msg;

export default instance;
export {
  msg,
};

/**
 * Fetches a resource's data by path.
 * @param resource The resource info.
 * @param fallback The fallback value in case of the fetch failing.
 */
async function fetchResourceData(resource: LanguageResource, fallback: any): Promise<any> {
  const response = await fetch(resource.location);

  if (response.ok) {
    return await response.json();
  }
  return fallback;
}

interface LanguageDelta {
  data: Record<string, string>;
  language: string;
}

/**
 * Fetches the language data, overlapping base and locale-specific data.
 * @param languages An object of resource path values, indexed by language.
 * @param language The language to fetch.
 */
export async function fetchLanguageDeltas(languages: Record<string, string>, language: string): Promise<LanguageDelta[]> {
  const resources = languageResources(languages, language);
  const promises = resources.map(async resource => {
    const data = await fetchResourceData(resource, {});
    return { data, language: resource.language };
  });
  return await Promise.all(promises);
}

/**
 * @see https://developer.mozilla.org/en-US/docs/Web/API/NavigatorLanguage/language
 */
function getBrowserLanguage(): string {
  return navigator.language;
}

function interpolate(expression: string, args: Record<string, any> = {}): string {
  if (isEmptyObject(args)) {
    return expression;
  }

  const names = Object.keys(args);
  const values = Object.values(args);
  try {
    return new Function(...names, `return \`${expression}\`;`)(...values);
  } catch(e) {
    return expression;
  }
}

interface LanguageResource {
  language: string;
  location: string;
}

/**
 * Language resource file paths to fetch.
 * Finds the paths to resources that belong to the specified language, **ordered from generic to specific**.
 * @param languages An object of resource path values, indexed by language.
 * @param language The language which the resource files should represent.
 */
function languageResources(languages: Record<string, string>, language: string): LanguageResource[] {
  const paths: LanguageResource[] = [];

  if (language in languages) {
    paths.push({ language, location: languages[language] });
  }

  const [baseLanguage] = language.split('-');

  if (baseLanguage in languages) {
    paths.unshift({ language: baseLanguage, location: languages[baseLanguage] });
  }

  return paths;
}

/**
 * Normalizes language keys for safe and assertive comparisons.
 * Currently just uses **lower case**.
 * @param key The key to normalize.
 */
function normalizeLanguageKey(key: string): string {
  return key.toLowerCase();
}

/**
 * Normalizes defaultLanguage and all language keys.
 * @param config The config to normalize.
 */
function normalizeConfig(config: MessageServiceConfig): MessageServiceConfig {
  config.defaultLanguage = normalizeLanguageKey(config.defaultLanguage);

  for (const key in config.languages) {
    if (!Object.prototype.hasOwnProperty.call(config.languages, key)) {
      continue;
    }
    config.languages[normalizeLanguageKey(key)] = config.languages[key];
  }

  return config;
}
